/**
 * Session Warning Context
 *
 * Provides global state management for session countdown warnings.
 * Integrates with SessionTimerService to provide React hooks for components.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { sessionTimer, SessionInfo, SessionWarningLevel } from '../services/SessionTimerService';
import { logger } from "../utils/logger";
// import { logger } from "../utils/logger";
export interface SessionWarningState {
    timeRemaining: number | null;
    isWarningVisible: boolean;
    isExpiring: boolean;
    warningLevel: SessionWarningLevel;
    lastActivity: Date | null;
    sessionInfo: SessionInfo | null;
    expirationTime: Date | null;
}
export interface SessionWarningContextValue extends SessionWarningState {
    refreshSession: () => Promise<boolean>;
    dismissWarning: () => void;
    showWarning: () => void;
}
const SessionWarningContext = createContext<SessionWarningContextValue | undefined>(undefined);
export interface SessionWarningProviderProps {
    children: ReactNode;
}
export const SessionWarningProvider: React.FC<SessionWarningProviderProps> = ({ children }) => {
    const [state, setState] = useState<SessionWarningState>({
        timeRemaining: null,
        isWarningVisible: false,
        isExpiring: false,
        warningLevel: SessionWarningLevel.NONE,
        lastActivity: null,
        sessionInfo: null,
        expirationTime: null
    });
    /**
     * Update time remaining every second when warning is active
     */
    useEffect(() => {
        if (!state.isWarningVisible) {
            return;
        }
        const updateInterval = setInterval(() => {
            const remaining = sessionTimer.getTimeRemaining();
            const level = sessionTimer.getWarningLevel();
            setState(prev => ({
                ...prev,
                timeRemaining: remaining,
                warningLevel: level,
                isExpiring: remaining < 60000 // < 1 minute
            }));
        }, 1000); // Update every second
        return () => clearInterval(updateInterval);
    }, [state.isWarningVisible]);
    /**
     * Handle session warning from SessionTimerService
     */
    const handleWarning = useCallback((timeRemaining: number, level: SessionWarningLevel) => {
        logger.auth('⚠️ Session warning received:', { "arg0": level, "arg1": 'Time remaining:', "arg2": Math.floor(timeRemaining / 1000) + 's' });
        setState(prev => ({
            ...prev,
            timeRemaining,
            isWarningVisible: true,
            warningLevel: level,
            isExpiring: timeRemaining < 60000,
            expirationTime: sessionTimer.getExpirationTime()
        }));
    }, []);
    /**
     * Handle session expiration
     */
    const handleExpired = useCallback(() => {
        logger.auth('⏱️ Session expired');
        setState(prev => ({
            ...prev,
            timeRemaining: 0,
            isWarningVisible: false,
            isExpiring: false,
            warningLevel: SessionWarningLevel.NONE
        }));
    }, []);
    /**
     * Handle session refresh
     */
    const handleRefreshed = useCallback((newExpiresAt: Date) => {
        logger.auth('✅ Session refreshed, expires at:', { "arg0": newExpiresAt.toISOString() });
        setState(prev => ({
            ...prev,
            timeRemaining: null,
            isWarningVisible: false,
            isExpiring: false,
            warningLevel: SessionWarningLevel.NONE,
            expirationTime: newExpiresAt
        }));
    }, []);
    /**
     * Set up listeners for session timer events
     */
    useEffect(() => {
        const unsubscribeWarning = sessionTimer.onWarning(handleWarning);
        const unsubscribeExpired = sessionTimer.onExpired(handleExpired);
        const unsubscribeRefreshed = sessionTimer.onRefreshed(handleRefreshed);
        // Initialize session timer
        sessionTimer.initialize().catch(error => {
            logger.auth('Failed to initialize session timer:', { "arg0": error });
        });
        return () => {
            unsubscribeWarning();
            unsubscribeExpired();
            unsubscribeRefreshed();
        };
    }, [handleWarning, handleExpired, handleRefreshed]);
    /**
     * Refresh session manually
     */
    const refreshSession = useCallback(async (): Promise<boolean> => {
        const success = await sessionTimer.refresh();
        return success;
    }, []);
    /**
     * Dismiss warning (only allowed if > 3 minutes remaining)
     */
    const dismissWarning = useCallback(() => {
        const remaining = sessionTimer.getTimeRemaining();
        // Only allow dismiss if > 3 minutes remaining
        if (remaining > 3 * 60 * 1000) {
            setState(prev => ({
                ...prev,
                isWarningVisible: false
            }));
        }
    }, []);
    /**
     * Show warning manually
     */
    const showWarning = useCallback(() => {
        setState(prev => ({
            ...prev,
            isWarningVisible: true,
            timeRemaining: sessionTimer.getTimeRemaining(),
            warningLevel: sessionTimer.getWarningLevel()
        }));
    }, []);
    const value: SessionWarningContextValue = {
        ...state,
        refreshSession,
        dismissWarning,
        showWarning
    };
    return (<SessionWarningContext.Provider value={value}>
      {children}
    </SessionWarningContext.Provider>);
};
/**
 * Hook to access session warning context
 */
export function useSessionWarning(): SessionWarningContextValue {
    const context = useContext(SessionWarningContext);
    if (context === undefined) {
        // During HMR (Hot Module Replacement) in development, the context might be undefined temporarily
        // Return a fallback value instead of throwing an error
        if (import.meta.env.DEV) {
            logger.warn('useSessionWarning called outside SessionWarningProvider during hot reload, returning fallback');
            return {
                isWarningVisible: false,
                timeRemaining: null,
                expirationTime: null,
                warningLevel: 'none',
                isExpiring: false,
                refreshSession: async () => { },
                dismissWarning: () => { }
            };
        }
        throw new Error('useSessionWarning must be used within a SessionWarningProvider');
    }
    return context;
}
/**
 * Hook to get session timer
 */
export function useSessionTimer() {
    const { timeRemaining, expirationTime, warningLevel } = useSessionWarning();
    return {
        timeRemaining,
        expirationTime,
        warningLevel,
        timeRemainingSeconds: timeRemaining ? Math.floor(timeRemaining / 1000) : 0
    };
}
/**
 * Hook to refresh session
 */
export function useSessionRefresh() {
    const { refreshSession } = useSessionWarning();
    return refreshSession;
}
export default SessionWarningContext;
